import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import {
  UsersRound, Plus, Pencil, X, Save, Check,
  Phone, Mail, Shield, User, AlertTriangle,
} from 'lucide-react'

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLES = ['admin', 'labor']

const ROLE_LABELS = { admin: 'Admin', labor: 'Labor' }

const ROLE_BADGE = {
  admin: { background: '#EFF6FF', color: '#1D4ED8', borderColor: '#BFDBFE' },
  labor: { background: '#F0FDF4', color: '#15803D', borderColor: '#BBF7D0' },
}

// ─── Add/Edit Staff Modal ─────────────────────────────────────────────────────

function StaffModal({ existing, onClose, onSaved }) {
  const isNew = !existing

  const [fullName, setFullName]   = useState(existing?.full_name || '')
  const [phone,    setPhone]      = useState(existing?.phone || '')
  const [role,     setRole]       = useState(existing?.role || 'labor')
  const [active,   setActive]     = useState(existing?.active ?? true)

  // For new staff only
  const [email,    setEmail]      = useState('')
  const [password, setPassword]   = useState('')

  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')

  async function handleSave(e) {
    e.preventDefault()
    if (!fullName.trim()) return setError('Full name is required.')
    if (isNew && !email.trim()) return setError('Email is required.')
    if (isNew && password.length < 8) return setError('Password must be at least 8 characters.')

    setSaving(true)
    setError('')

    if (isNew) {
      // Sign up the new user. supabase.auth.signUp in v2 does NOT sign out the
      // currently authenticated user (since the session is maintained separately).
      // The new account will be inactive until the admin activates it if email
      // confirmation is enabled. Disable email confirmation in Supabase dashboard
      // (Auth → Settings → "Enable email confirmations" off) for this flow.
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: { full_name: fullName.trim(), role },
        },
      })

      if (signUpErr) {
        setSaving(false)
        return setError(signUpErr.message)
      }

      // The handle_new_user trigger creates the users row automatically.
      // Update phone + active immediately (trigger may not set these).
      if (signUpData?.user?.id) {
        await supabase.from('users')
          .update({ full_name: fullName.trim(), phone: phone.trim() || null, role, active })
          .eq('id', signUpData.user.id)
      }
    } else {
      // Edit existing user profile
      const { error: updateErr } = await supabase.from('users')
        .update({ full_name: fullName.trim(), phone: phone.trim() || null, role, active })
        .eq('id', existing.id)

      if (updateErr) {
        setSaving(false)
        return setError(updateErr.message)
      }
    }

    setSaving(false)
    onSaved()
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>
            {isNew ? 'Add Staff Member' : 'Edit Staff Member'}
          </h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={handleSave} style={{ padding: '20px 24px' }}>
          {error && (
            <div className="alert alert-error" style={{ marginBottom: 16 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0 }} />
              {error}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Full Name *</label>
            <input className="form-input" value={fullName} onChange={e => setFullName(e.target.value)}
              placeholder="e.g. Jamie Anderson" required />
          </div>

          {isNew && (
            <>
              <div className="form-group">
                <label className="form-label">Email *</label>
                <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="jamie@example.com" required />
              </div>
              <div className="form-group">
                <label className="form-label">Temporary Password *</label>
                <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 8 characters" required />
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                  Share this with the staff member so they can log in and change it.
                </div>
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="204-555-0000" />
          </div>

          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
            </select>
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 4 }}>
              Admin: full CRM access. Labor: My Jobs view only.
            </div>
          </div>

          {!isNew && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}>
                <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }} />
                <span className="form-label" style={{ marginBottom: 0 }}>Active (can be assigned to jobs)</span>
              </label>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isNew ? 'Create Staff Member' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Staff Row ────────────────────────────────────────────────────────────────

function StaffRow({ member, onEdit }) {
  const badge = ROLE_BADGE[member.role] || ROLE_BADGE.labor

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '14px 20px', borderBottom: '1px solid var(--color-border)',
      opacity: member.active ? 1 : 0.55,
    }}>
      {/* Avatar */}
      <div style={{
        width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
        background: member.active ? 'var(--color-navy)' : 'var(--color-border-strong)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'white', fontWeight: 700, fontSize: 15,
      }}>
        {(member.full_name || 'U').charAt(0).toUpperCase()}
      </div>

      {/* Name + meta */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{member.full_name}</span>
          <span style={{
            fontSize: 'var(--text-xs)', fontWeight: 600,
            padding: '2px 8px', borderRadius: 12,
            border: `1px solid ${badge.borderColor}`,
            background: badge.background, color: badge.color,
          }}>
            {member.role === 'admin' && <Shield size={10} style={{ marginRight: 3 }} />}
            {ROLE_LABELS[member.role]}
          </span>
          {!member.active && (
            <span style={{
              fontSize: 'var(--text-xs)', padding: '2px 8px', borderRadius: 12,
              background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA', fontWeight: 600,
            }}>Inactive</span>
          )}
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 3, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {member.email && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Mail size={11} /> {member.email}
            </span>
          )}
          {member.phone && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Phone size={11} /> {member.phone}
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <button className="btn btn-ghost btn-sm" onClick={() => onEdit(member)}
        style={{ flexShrink: 0 }}>
        <Pencil size={14} />
      </button>
    </div>
  )
}

// ─── StaffPage ────────────────────────────────────────────────────────────────

export default function StaffPage() {
  const [staff,     setStaff]     = useState([])
  const [loading,   setLoading]   = useState(true)
  const [modal,     setModal]     = useState(null)   // null | 'add' | {member}
  const [filter,    setFilter]    = useState('all')  // 'all' | 'admin' | 'labor'
  const [saved,     setSaved]     = useState(false)
  const [error,     setError]     = useState('')
  const [jobCounts, setJobCounts] = useState({})     // userId -> count of upcoming jobs

  const load = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const { data, error: usersErr } = await supabase
        .from('users')
        .select('*')
        .order('full_name')

      if (usersErr) throw usersErr
      setStaff(data || [])

      // Load upcoming confirmed job counts per staff member
      const today = new Date().toISOString().slice(0, 10)
      const { data: assignments, error: assignmentsErr } = await supabase
        .from('job_staff')
        .select('user_id, job_id, jobs!inner(event_date, status)')
        .eq('status', 'confirmed')
        .gte('jobs.event_date', today)
        .neq('jobs.status', 'cancelled')

      if (assignmentsErr) throw assignmentsErr

      const counts = {}
      ;(assignments || []).forEach(a => {
        counts[a.user_id] = (counts[a.user_id] || 0) + 1
      })
      setJobCounts(counts)
    } catch (err) {
      console.error('Failed to load staff data:', err)
      setError(err?.message || 'Failed to load staff data.')
      setStaff([])
      setJobCounts({})
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  function handleSaved() {
    setModal(null)
    setSaved(true)
    load()
    setTimeout(() => setSaved(false), 3000)
  }

  const visible = staff.filter(m => {
    if (filter === 'admin') return m.role === 'admin'
    if (filter === 'labor') return m.role === 'labor'
    return true
  })

  const activeCount   = staff.filter(m => m.active).length
  const laborCount    = staff.filter(m => m.role === 'labor' && m.active).length
  const adminCount    = staff.filter(m => m.role === 'admin').length

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Staff</h1>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', marginTop: 2 }}>
            Manage your crew roster and system users.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('add')}>
          <Plus size={16} /> Add Staff Member
        </button>
      </div>

      {saved && (
        <div className="alert alert-success" style={{ marginBottom: 16 }}>
          <Check size={14} /> Staff member saved successfully.
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Active Staff', value: activeCount, icon: UsersRound },
          { label: 'Labor Crew',   value: laborCount,  icon: User },
          { label: 'Admins',       value: adminCount,  icon: Shield },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="card" style={{ padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8, background: 'var(--color-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid var(--color-border)',
              }}>
                <Icon size={16} style={{ color: 'var(--color-text-muted)' }} />
              </div>
              <div>
                <div style={{ fontSize: 'var(--text-xl)', fontWeight: 800, color: 'var(--color-navy)' }}>{value}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{
          display: 'flex', gap: 0, padding: '12px 20px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg)',
        }}>
          {[
            { key: 'all',   label: `All (${staff.length})` },
            { key: 'labor', label: `Labor (${staff.filter(m => m.role === 'labor').length})` },
            { key: 'admin', label: `Admin (${adminCount})` },
          ].map(tab => (
            <button key={tab.key}
              className={`btn btn-sm ${filter === tab.key ? 'btn-primary' : 'btn-ghost'}`}
              style={{ marginRight: 6, borderRadius: 6 }}
              onClick={() => setFilter(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            Loading staff…
          </div>
        ) : visible.length === 0 ? (
          <div className="empty-state" style={{ padding: '48px 20px' }}>
            <UsersRound size={36} className="empty-state-icon" />
            <p>No staff members found.</p>
            <button className="btn btn-primary btn-sm" onClick={() => setModal('add')}>
              <Plus size={14} /> Add First Staff Member
            </button>
          </div>
        ) : (
          <div>
            {visible.map(m => (
              <StaffRow
                key={m.id}
                member={{ ...m, _jobCount: jobCounts[m.id] || 0 }}
                onEdit={member => setModal(member)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Note about email confirmation */}
      <div className="alert alert-info" style={{ marginTop: 16 }}>
        <div>
          <strong>Note:</strong> To allow instant staff creation without email confirmation, go to your Supabase dashboard →
          Authentication → Settings → and disable <em>"Enable email confirmations"</em>. Otherwise new accounts
          require the user to verify their email before logging in.
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <StaffModal
          existing={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
